"use client";
import React, { Fragment, useEffect, useRef, useState } from "react";
import Image from "next/image";
import PropTypes from "prop-types";
import cn from "classnames";
import { createPortal } from "react-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import TextareaAutosize from "react-textarea-autosize";

import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";

import useWindowDimensions from "@/next-src/hooks/useWindowDimensions";
import { useNotifyState, useNotifyUpdate } from "@/next-src/context/Notify";

import Button from "@/next-src/components/ui/button/Button";
import ResponsiveImage from "@/next-src/components/ui/responsive-image/ResponsiveImage";

import comCSS from "./NotifyModal.module.scss";

const Notify = ({ className = "", data }) => {
  const { isShown } = useNotifyState();
  const { setIsShown } = useNotifyUpdate();
  const { width: windowWidth } = useWindowDimensions();
  const [isSent, setIsSent] = useState(false);
  const {
    titleMobile,
    textMobile,
    form: {
      buttonMobile,
      buttonSubmitingMobile,
      buttonSuccessMobile,
      email,
      firstname,
      lastname,
      cityOfResidence,
      questionnaire,
    },
    image,
  } = data || {};

  let {
    title,
    text,
    form: { button, buttonSubmiting, buttonSuccess },
  } = data || {};

  if (windowWidth < 992) {
    title = titleMobile;
    text = textMobile;
    button = buttonMobile;
    buttonSubmiting = buttonSubmitingMobile;
    buttonSuccess = buttonSuccessMobile;
  }

  const fieldsData = [
    { field: email, key: "email", errMsg: "Email is required" },
    { field: firstname, key: "firstname", errMsg: "First name is required" },
    { field: lastname, key: "lastname", errMsg: "Last name is required" },
    {
      field: cityOfResidence,
      key: "cityOfResidence",
      errMsg: "City of Residence is required",
    },
    ...(questionnaire || []).map((el, i) => ({
      field: el,
      key: `question${i + 1}`,
      errMsg: "Is required",
    })),
  ];

  // Define validation schema using Yup
  const preValidationSchema = {};
  if (!email.hide) {
    let emailValidation = Yup.string().email("Invalid email");
    if (email.isRequired) {
      emailValidation = emailValidation.required("Email is required");
    }
    preValidationSchema.email = emailValidation;
  }

  fieldsData
    .filter((el) => !el.field?.hide && el.key !== "email")
    .forEach((el) => {
      if (el.field?.isRequired) {
        preValidationSchema[el.key] = Yup.string().required(el.errMsg);
      } else {
        preValidationSchema[el.key] = Yup.string();
      }
    });

  const validationSchema = Yup.object().shape(preValidationSchema);

  // Initial form values
  const initialValues = fieldsData
    .filter((el) => !el.field?.hide)
    .reduce((acc, cur) => {
      acc[cur.key] = "";
      return acc;
    }, {});

  const onSubmit = async (values, { resetForm, setSubmitting }) => {
    const questions = questionnaire
      ?.filter((el) => !el.hide)
      ?.map((el, i) => ({
        question: el.placeholder,
        answer: values[`question${i + 1}`],
      }));
    const reqData = {
      ...values,
      url: window.location?.toString() || "",
      questionnaire: questions,
    };
    try {
      const res = await axios({
        method: "POST",
        data: reqData,
        url: "/api/newsletter-subscriptions",
      });
      if (res) {
        setIsSent(true);
        setTimeout(() => {
          setIsSent(false);
        }, 8000);
        resetForm();
      }
    } catch (error) {
      console.log("error", error);
    }
    setSubmitting(false); // Important: This line tells Formik that submission is complete
  };

  const getButton = (isSubmitting) => {
    if (!isSubmitting && !isSent)
      return (
        <Button
          type="submit"
          data={button}
          disabled={isSubmitting}
          className="button is-inverted w-button"
        />
      );
    if (isSubmitting)
      return (
        <Button
          type="submit"
          data={buttonSubmiting}
          disabled={isSubmitting}
          className="button is-inverted w-button"
        />
      );
    if (isSent)
      return (
        <Button
          type="submit"
          data={buttonSuccess}
          className="button is-inverted w-button"
        />
      );
    return null;
  };
  return (
    <div
      className={cn(className, comCSS.wrapper, "download-brochure-pop-up", {
        [comCSS.active]: isShown,
      })}
    >
      <div
        className={cn("notify-modal-wrapper", comCSS.modalWrapper, {
          [comCSS.active]: isShown,
        })}
      >
        <section className="section">
          <div className="padding-global">
            <div className="container-large">
              <div className="custom-to-nav-section-padding">
                <div className="download-brochure-overflow-auto-wrap">
                  <div className={cn("brochure-download-card", comCSS.card)}>
                    <div
                      className={cn(
                        "download-brochure-form-wrap",
                        comCSS.formWrap
                      )}
                    >
                      <div className="brochure-form-heading-wrap">
                        <div className="margin-bottom margin-xsmall">
                          {convertHTMLtoJSX(title)}
                        </div>
                        <div className="max-width-small">
                          {convertHTMLtoJSX(text)}
                        </div>
                      </div>
                      <div
                        id="download-brochure-form"
                        className="cta-form-block no-max-width w-form"
                      >
                        <Formik
                          initialValues={initialValues}
                          validationSchema={validationSchema}
                          onSubmit={onSubmit}
                        >
                          {({
                            isSubmitting,
                            values,
                            handleChange,
                            errors,
                            touched,
                          }) => (
                            <Form
                              id="wf-form-Download-Brochure-Form"
                              name="wf-form-Download-Brochure-Form"
                              data-name="Download Brochure Form"
                              className="cta_form"
                              data-wf-page-id="63d939480c18d8d2008bd97b"
                              data-wf-element-id="6314b862-c672-3e38-68ee-a5f17085997d"
                              aria-label="Download Brochure Form"
                            >
                              <div className="margin-top margin-medium">
                                <div
                                  className={cn(
                                    "form-input-double-wrap no-gap",
                                    comCSS.inputDouble
                                  )}
                                >
                                  {!firstname.hide && (
                                    <div>
                                      <Field
                                        className="form-input is-custom no-left-padding-desktop w-input"
                                        maxLength="256"
                                        name="firstname"
                                        placeholder={firstname?.placeholder}
                                        type="text"
                                        id="first-name-4"
                                      />
                                      <div className="form-row-divider" />
                                      <ErrorMessage
                                        name="firstname"
                                        component="div"
                                        className={comCSS.error}
                                      />
                                    </div>
                                  )}
                                  {!lastname.hide && (
                                    <div>
                                      <Field
                                        className="form-input is-custom no-left-padding-desktop w-input"
                                        maxLength="256"
                                        name="lastname"
                                        placeholder={lastname?.placeholder}
                                        type="text"
                                        id="last-name-3"
                                      />
                                      <div className="form-row-divider" />
                                      <ErrorMessage
                                        name="lastname"
                                        component="div"
                                        className={comCSS.error}
                                      />
                                    </div>
                                  )}
                                  {!cityOfResidence.hide && (
                                    <div>
                                      <Field
                                        className="form-input is-custom no-left-padding-desktop w-input"
                                        maxLength="256"
                                        name="cityOfResidence"
                                        placeholder={
                                          cityOfResidence?.placeholder
                                        }
                                        id="cityOfResidence"
                                      />
                                      <div className="form-row-divider" />
                                      <ErrorMessage
                                        name="cityOfResidence"
                                        component="div"
                                        className={comCSS.error}
                                      />
                                    </div>
                                  )}
                                </div>
                                {!email.hide && (
                                  <>
                                    <Field
                                      className="form-input is-custom no-left-padding-desktop w-input"
                                      maxLength="256"
                                      name="email"
                                      placeholder={email?.placeholder}
                                      type="email"
                                      id="email-6"
                                    />
                                    <div className="form-row-divider" />
                                    <ErrorMessage
                                      name="email"
                                      component="div"
                                      className={comCSS.error}
                                    />
                                  </>
                                )}

                                {questionnaire
                                  ?.filter((el) => !el.hide)
                                  ?.map((el, i) => (
                                    <Fragment key={el.id}>
                                      <TextareaAutosize
                                        name={`question${i + 1}`}
                                        cols="40"
                                        minRows={2}
                                        value={values[`question${i + 1}`]}
                                        onChange={(e) => {
                                          handleChange(e);
                                        }}
                                        placeholder={el.placeholder}
                                        className={cn(
                                          comCSS.formControlTextarea,
                                          "form-input is-custom no-left-padding-desktop w-input"
                                        )}
                                      />
                                      <div className="form-row-divider" />
                                      {touched[`question${i + 1}`] &&
                                        errors[`question${i + 1}`] && (
                                          <div className={comCSS.error}>
                                            {errors[`question${i + 1}`]}
                                          </div>
                                        )}
                                    </Fragment>
                                  ))}
                                <div className="contact-form-line-wrap distribute no-left-padding-desktop">
                                  <div />
                                  {getButton(isSubmitting, isSent)}
                                </div>
                              </div>
                            </Form>
                          )}
                        </Formik>

                        {/* <div
                          className="success-message brochure-download w-form-done"
                          tabIndex="-1"
                          role="region"
                          aria-label="Download Brochure Form success"
                        >
                          <div className="success-message-wrap">
                            <div className="rounded-icon is-success">
                              <Image
                              width={24}
                              height={24}
                                src="/images/home/64217eda6726034a4ce379d4_dark-green-check.svg"
                                loading="lazy"
                                alt=""
                              />
                            </div>
                            <div>
                              <div className="margin-bottom margin-tiny">
                                <h5>Successfully sent</h5>
                              </div>
                              <p>
                                If you have any questions, don&amp;t hesitate to{" "}
                                <a
                                  href="/contact"
                                  className="text-style-link is-blue"
                                >
                                  contact us
                                </a>
                                .
                              </p>
                            </div>
                          </div>
                        </div>
                        <div
                          className="error-message w-form-fail"
                          tabIndex="-1"
                          role="region"
                          aria-label="Download Brochure Form failure"
                        >
                          <div className="error-text">
                            Oops! Something went wrong while submitting the
                            form?.
                          </div>
                        </div> */}
                      </div>
                    </div>
                    <div
                      id="w-node-_289feebd-30be-03c0-d179-8bdc59f1a12c-59f1a10d"
                      className="cta-brochure-img"
                    >
                      <ResponsiveImage
                        width={640}
                        height={560}
                        src={image?.src}
                        lazyLoading
                        sizes="(min-width: 992px) 640px, 100vw"
                        alt={image?.alt}
                        className="cover-image"
                      />
                    </div>
                    <a
                      data-w-id="289feebd-30be-03c0-d179-8bdc59f1a12e"
                      href="#"
                      className="close-btn-link position-abs-right-top-margins w-inline-block"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsShown(false);
                      }}
                    >
                      <Image
                        width={24}
                        height={24}
                        src="/images/home/63db8bbb24c0a691e0cc2cc8_close-icon.svg"
                        loading="lazy"
                        alt=""
                        className="icon-1x1-xsmall"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <div
        data-w-id="289feebd-30be-03c0-d179-8bdc59f1a130"
        className="download-brochure-pop-up-bg"
        style={{ opacity: 1 }}
        onClick={() => setIsShown(false)}
      />
    </div>
  );
};

Notify.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    title: PropTypes.node,
    titleMobile: PropTypes.node,
    text: PropTypes.node,
    textMobile: PropTypes.node,
    file: PropTypes.shape({
      type: PropTypes.string,
      size: PropTypes.string,
    }),
    image: PropTypes.shape({
      id: PropTypes.string,
      src: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
    }),
    form: PropTypes.shape({
      email: PropTypes.shape({
        placeholder: PropTypes.string,
        isRequired: PropTypes.bool,
        hide: PropTypes.bool,
      }),
      firstname: PropTypes.shape({
        placeholder: PropTypes.string,
        isRequired: PropTypes.bool,
        hide: PropTypes.bool,
      }),
      lastname: PropTypes.shape({
        placeholder: PropTypes.string,
        isRequired: PropTypes.bool,
        hide: PropTypes.bool,
      }),
      cityOfResidence: PropTypes.shape({
        placeholder: PropTypes.string,
        isRequired: PropTypes.bool,
        hide: PropTypes.bool,
      }),
      questionnaire: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string, // Questionnaire item ID (optional)
          placeholder: PropTypes.string, // Questionnaire item placeholder (optional)
          isRequired: PropTypes.bool, // Indicates if questionnaire item is required
          hide: PropTypes.bool, // Indicates if questionnaire item should be hidden
        })
      ),
      privacyPolicyText: PropTypes.node, // Allows for various privacy policy content (e.g., strings or JSX)
      button: PropTypes.shape({
        id: PropTypes.string,
        text: PropTypes.node, // Allows for various button text content (e.g., strings or JSX)
        url: PropTypes.string,
        bgColor: PropTypes.string,
        bgColorHover: PropTypes.string,
        borderColor: PropTypes.string,
        borderColorHover: PropTypes.string,
        hide: PropTypes.bool,
      }),
      buttonMobile: PropTypes.shape({
        id: PropTypes.string,
        text: PropTypes.node, // Allows for various button text content (e.g., strings or JSX)
        url: PropTypes.string,
        bgColor: PropTypes.string,
        bgColorHover: PropTypes.string,
        borderColor: PropTypes.string,
        borderColorHover: PropTypes.string,
        hide: PropTypes.bool,
      }),
      buttonSubmiting: PropTypes.shape({
        id: PropTypes.string,
        text: PropTypes.node, // Allows for various button text content (e.g., strings or JSX)
        url: PropTypes.string,
        bgColor: PropTypes.string,
        bgColorHover: PropTypes.string,
        borderColor: PropTypes.string,
        borderColorHover: PropTypes.string,
        hide: PropTypes.bool,
      }),
      buttonSubmitingMobile: PropTypes.shape({
        id: PropTypes.string,
        text: PropTypes.node, // Allows for various button text content (e.g., strings or JSX)
        url: PropTypes.string,
        bgColor: PropTypes.string,
        bgColorHover: PropTypes.string,
        borderColor: PropTypes.string,
        borderColorHover: PropTypes.string,
        hide: PropTypes.bool,
      }),
      buttonSuccess: PropTypes.shape({
        id: PropTypes.string,
        text: PropTypes.node, // Allows for various button text content (e.g., strings or JSX)
        url: PropTypes.string,
        bgColor: PropTypes.string,
        bgColorHover: PropTypes.string,
        borderColor: PropTypes.string,
        borderColorHover: PropTypes.string,
        hide: PropTypes.bool,
      }),
      buttonSuccessMobile: PropTypes.shape({
        id: PropTypes.string,
        text: PropTypes.node, // Allows for various button text content (e.g., strings or JSX)
        url: PropTypes.string,
        bgColor: PropTypes.string,
        bgColorHover: PropTypes.string,
        borderColor: PropTypes.string,
        borderColorHover: PropTypes.string,
        hide: PropTypes.bool,
      }),
      brochureLink: PropTypes.string,
    }),
  }),
};

const NotifyModal = ({ className = "", data }) => {
  const ref = useRef(null);
  const { isShown } = useNotifyState();

  useEffect(() => {
    const selector = "notify-modal-wrapper";
    let domElWrapper = document.getElementById(selector);
    if (!domElWrapper) {
      domElWrapper = document.createElement("div");
      domElWrapper.id = selector;
      document.body.appendChild(domElWrapper);
    }
    ref.current = domElWrapper;
  }, []);

  return ref.current
    ? createPortal(<Notify className={className} data={data} />, ref.current)
    : null;
};

NotifyModal.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({}),
};

export default NotifyModal;
